import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { Loader2 } from 'lucide-react';

/**
 * Handles the redirect from the Google OAuth callback.
 * The server redirects to /auth/callback#token=...&refreshToken=... — the URL
 * FRAGMENT is used (not query params) so the tokens are never sent to servers
 * or leaked into logs/history/referrers. This page extracts the tokens, stores
 * them, and redirects to the dashboard. Query params are still accepted as a
 * fallback for any old links.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const setState = useAuthStore.setState;

  useEffect(() => {
    // Prefer the fragment, then fall back to query params
    const hash = window.location.hash.replace(/^#/, '');
    let token = hash ? new URLSearchParams(hash).get('token') : null;
    let refreshToken = hash ? new URLSearchParams(hash).get('refreshToken') : null;

    if (!token || !refreshToken) {
      token = searchParams.get('token');
      refreshToken = searchParams.get('refreshToken');
    }

    if (!token || !refreshToken) {
      setError('Authentication failed — missing tokens. Please try signing in again.');
      return;
    }

    // Store tokens and decode user from JWT
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const user = {
        id: payload.userId,
        email: payload.email,
        name: payload.email, // Will be updated by fetchUser
        role: payload.role,
      };

      setState({
        token,
        refreshToken,
        user,
        isAuthenticated: true,
      });

      // Navigate to dashboard — the DashboardLayout will trigger fetchUser for full user info
      navigate('/', { replace: true });
    } catch (err) {
      setError('Failed to process authentication. Please try again.');
    }
  }, [searchParams, navigate, setState]);

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-400 text-xl">!</span>
          </div>
          <h2 className="text-lg font-bold mb-2">Authentication Failed</h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">{error}</p>
          <a
            href="/login"
            className="text-sm text-dyndev-green hover:underline"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 size={32} className="animate-spin text-dyndev-green mx-auto mb-4" />
        <p className="text-sm text-[var(--text-secondary)]">Signing you in with Google…</p>
      </div>
    </div>
  );
}
